import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Col, Form, Label, Row } from "reactstrap";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import ControlledRowTextField from "../../../../components/ControlledRowTextField";
import Select from "react-select";
import {
    dineTypeForMenu,
    MenuTypeForMenu,
} from "../../../../dummyData/dummyData";
import {
    useUpdateMenuMutation,
    useLazyGetMenuItemsListQuery,
} from "../../../../redux/services/MenusService";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import TimeRangeSelector from "../../../../components/TimeRangeSelector";
import ButtonComponent from "../../../../components/ButtonComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faSpinner, faTrash, faGripVertical } from "@fortawesome/free-solid-svg-icons";
import { Menu, MenuItem } from "../MenusTable";
import { toast } from "react-toastify";
import { customFilterOption } from "../../../../utility/Utility";
import { getImageUrl } from "../../../../dummyData/FoodAttributes";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import TimeSelector from "../../../../components/TimeSelector";
import { brandOptions, MENU_SUPPORTED_APPS } from "../../../../constants/constans";

type LocationState = {
    menu?: Menu;
};

interface FormData {
    displayName: string;
    servingStartTime: string;
    servingEndTime: string;
    maxDaysToScheduleAhead: number;
    orderClosingTime: string;
}

const UpdateMenus: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { menu } = location.state as LocationState;

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues: {
            displayName: menu?.displayName || "",
            servingStartTime: menu ? menu.servingTime.split("-")[0] : "",
            servingEndTime: menu ? menu.servingTime.split("-")[1] : "",
            maxDaysToScheduleAhead: menu?.maxDaysToScheduleAhead || 0,
        },
    });

    const [selectedOption, setSelectedOption] = useState<MenuItem | null>(null);
    const [overridePrice, setOverridePrice] = useState<string>("");
    const [selectedItems, setSelectedItems] = useState<MenuItem[]>(() => {
        // Add draggableId to existing items when loading from menu
        return (menu?.items || []).map((item: MenuItem, index: number) => ({
            ...item,
            draggableId: `draggable-loaded-${index}-${item.itemId?.replace(/[^a-zA-Z0-9]/g, '')}`
        }));
    });
    const [selectedDineType, setSelectedDineType] = useState<{
        value: string;
        label: string;
    } | null>(menu ? { value: menu.dineType, label: menu.dineType } : null);
    const [selectedBrandType, setSelectedBrandType] = useState<{
        value: string;
        label: string;
    } | null>(menu ? { value: menu.brand, label: menu.brand } : null);
    const [selectedSupportedApps, setSelectedSupportedApps] = useState<{
        value: string;
        label: string;
    }[]>(() => {
        if (menu && menu.supportedApps) {
            const supportedAppsArray = Array.isArray(menu.supportedApps) ? menu.supportedApps : [menu.supportedApps];
            return supportedAppsArray.filter(Boolean).map(ch => ({ value: ch, label: ch }));
        }
        return [];
    });
    const [selectedMenuTypeOption, setSelectedMenuTypeOption] = useState<{
        value: string;
        label: string;
    } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [itemError, setItemError] = useState<string | null>(null);
    const [brandTypeError, setBrandTypeError] = useState<string | null>(null);
    const [supportedAppsError, setSupportedAppsError] = useState<string | null>(null);
    const [dineTypeError, setDineTypeError] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [menuItemsOptionsForMenu, setMenuItemsOptionsForMenu] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const restaurantId = useSelector(
        (state: any) => state.restaurant.selectedRestaurantDetails.id
    );

    const [trigger, { isLoading }] = useLazyGetMenuItemsListQuery();
    const [updateMenu] = useUpdateMenuMutation();
    useEffect(() => {
        if (selectedMenuTypeOption) {
            trigger({ restaurantId, menuType: selectedMenuTypeOption.value })
                .unwrap()
                .then((MenuItems) => {
                    const menuItems = MenuItems.map((item: any) => ({
                        value: item.itemId,
                        label: (
                            <div
                                style={{ display: "flex", justifyContent: "left", gap: "5%" }}
                            >
                                <img
                                    src={item.imageUrl}
                                    alt={item.displayName}
                                    width="40px"
                                    height="40px"
                                />
                                {item.displayName}
                            </div>
                        ),
                        displayName: item.displayName,
                        tinyId: item.tinyId,
                        price: item.price,
                        itemId: item.itemId,
                        itemType: item.itemType,
                        imageUrl: item.imageUrl,
                        isSellingProduct: item.isSellingProduct,
                        allergenInformation: item.allergenInformation || [],
                        foodAttributes: item.foodAttributes || [],
                    }));
                    setMenuItemsOptionsForMenu(menuItems);
                })
                .catch((error) => {
                    toast.error("Failed to fetch menu items");
                });
        }
    }, [selectedMenuTypeOption, trigger, restaurantId]);

    const isMenuItemTypeSelected = selectedMenuTypeOption?.value === "Single";
    const availableMenuItemsOptions = menuItemsOptionsForMenu.filter(
        (item: any) =>
            !selectedItems.some(
                (selectedItem: any) => selectedItem.itemId === item.itemId
            ) &&
            (!isMenuItemTypeSelected || item.isSellingProduct === true)
    );

    const onSubmit = async (data: FormData) => {
        if (!selectedBrandType) {
            setBrandTypeError("Brand type must be selected");
            return;
        }
        if (!selectedDineType) {
            setDineTypeError("Dine type must be selected");
            return;
        }
        if (selectedItems.length === 0) {
            setItemError("At least one menu item must be selected");
            return;
        }

        setIsSaving(true);
        setItemError(null);
        setDineTypeError(null);
        setBrandTypeError(null);

        const updatedMenuData = {
            menuId: menu?.id || "",
            restaurantId: menu?.restaurantId || "",
            displayName: data.displayName,
            brand: selectedBrandType?.value as "Curry King" | "Wok & Flame" | "Rasa Bath",
            dineType: selectedDineType?.value || "",
            supportedApps: selectedSupportedApps.map(ch => ch.value),
            servingTime: `${data.servingStartTime}-${data.servingEndTime}`,
            orderClosingTime: data.orderClosingTime,
            maxDaysToScheduleAhead: +data.maxDaysToScheduleAhead,
            items: selectedItems.map((item) => {
                const itemData: any = {
                    displayName: item.displayName,
                    tinyId: item.tinyId,
                    price: item.price,
                    itemId: item.itemId,
                    itemType: item.itemType,
                    imageUrl: item.imageUrl,
                    allergenInformation: item.allergenInformation || [],
                    foodAttributes: item.foodAttributes || [],
                };
                if (item.overridePrice !== undefined) {
                    itemData.overridePrice = item.overridePrice;
                }
                return itemData;
            }),
        };

        try {
            await updateMenu(updatedMenuData).unwrap();
            toast.success("Menu updated successfully");
            navigate("/menu/menu-list");
        } catch (error: any) {
            if (error.status === 409) {
                toast.error("This menu name already used");
                return;
            }
            toast.error("Failed to update menu");
        } finally {
            setIsSaving(false);
        }
    };

    const handleAdd = () => {
        if (selectedOption) {
            const isDuplicate = selectedItems.some(
                (item) => item.displayName === selectedOption.displayName
            );
            if (isDuplicate) {
                setItemError("This item is already added");
            } else {
                // Add a unique draggableId to the item
                const itemWithId = {
                    ...selectedOption,
                    draggableId: `draggable-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    ...(overridePrice && { overridePrice: parseFloat(overridePrice) }),
                };
                setSelectedItems([...selectedItems, itemWithId]);
                setSelectedOption(null);
                setOverridePrice("");
                setItemError(null);
            }
        }
    };

    // const handleDelete = (index: number) => {
    //     const updatedItems = [...selectedItems];
    //     updatedItems.splice(index, 1);
    //     setSelectedItems(updatedItems);
    // };
    const handleDelete = (index: number) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                const updatedItems = [...selectedItems];
                updatedItems.splice(index, 1);
                setSelectedItems(updatedItems);

                Swal.fire("Deleted!", "Your item has been deleted.", "success");
            }
        });
    };

    const handleBrandChange = (
        option: { value: string; label: string } | null
    ) => {
        setSelectedBrandType(option);
        if (option) {
            setBrandTypeError(null);
        }
    };

    const handleDineTypeChange = (
        option: { value: string; label: string } | null
    ) => {
        setSelectedDineType(option);
        if (option) {
            setDineTypeError(null);
        }
    };

    const handleSupportedAppsChange = (
        options: readonly { value: string; label: string }[]
    ) => {
        setSelectedSupportedApps([...options]);
        if (options && options.length > 0) {
            setSupportedAppsError(null);
        }
    };

    const handleMenuTypeChange = (
        option: { value: string; label: string } | null
    ) => {
        setSelectedMenuTypeOption(option);
        if (option) {
            setItemError(null);
        }
    };

    const handleItemChange = (option: MenuItem | null) => {
        setSelectedOption(option);
        if (option) {
            setItemError(null);
            setOverridePrice("");
        }
    };

    const handleEdit = () => {
        setIsEditMode(!isEditMode);
    };

    const handleOverridePriceChange = (index: number, value: string) => {
        const updatedItems = [...selectedItems];
        if (value === "" || value === null) {
            // Remove overridePrice if empty
            delete updatedItems[index].overridePrice;
        } else {
            const parsedValue = parseFloat(value);
            if (!isNaN(parsedValue) && parsedValue >= 0) {
                updatedItems[index].overridePrice = parsedValue;
            }
        }
        setSelectedItems(updatedItems);
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }

        const items = [...selectedItems];
        const [draggedItem] = items.splice(draggedIndex, 1);
        items.splice(dropIndex, 0, draggedItem);

        setSelectedItems(items);
        setDraggedIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    return (
        <>
            <BreadCrumbs hasSubMenus={true} />
            <br />
            <div className="p-0">
                <Card>
                    <CardHeader className="d-flex justify-content-between bg-white">
                        <h4>Update Menu</h4>
                        <Button variant="light" onClick={handleEdit}>
                            <FontAwesomeIcon icon={faEdit} title="Edit" color="#699BF7" />
                        </Button>
                    </CardHeader>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <ControlledRowTextField
                                label="Menu Name"
                                inputName="displayName"
                                inputType="text"
                                control={control}
                                rules={{ required: "Menu Item is Required" }}
                                isDisabled={!isEditMode}
                            />
                            {errors.displayName && (
                                <p className="text-danger">
                                    {errors.displayName.message as string}
                                </p>
                            )}
                            <hr />
                            <Row className="align-items-center">
                                <Col lg="4">
                                    <Label className="pt-1 form-label">Brand</Label>
                                </Col>
                                <Col lg="8">
                                    <Select
                                        options={brandOptions}
                                        value={selectedBrandType}
                                        onChange={handleBrandChange}
                                        placeholder="Select Brand"
                                        isSearchable
                                        isDisabled={!isEditMode}
                                    />
                                </Col>
                            </Row>
                            {brandTypeError && (
                                <Row className="mt-2">
                                    <Col>
                                        <div className="text-danger">{brandTypeError}</div>
                                    </Col>
                                </Row>
                            )}
                            <hr />
                            <TimeRangeSelector
                                label="Serving Time"
                                inputName={["servingStartTime", "servingEndTime"]}
                                inputType="time"
                                placeholder={["Start Time", "End Time"]}
                                control={control}
                                rules={{ required: true }}
                                defaultValue={[
                                    menu?.servingTime.split("-")[0] || "",
                                    menu?.servingTime.split("-")[1] || "",
                                ]}
                                isDisabled={!isEditMode}
                            />
                            <hr />
                            <TimeSelector
                                label="Ordering Closes At"
                                inputName="orderClosingTime"
                                inputType="time"
                                placeholder="Select time"
                                control={control}
                                rules={{ required: "Time is required" }}
                                errors={errors}
                                isRequired={true}
                                hasSideLabel={false}
                                defaultValue={menu?.orderClosingTime || "10:30"}
                                isDisabled={!isEditMode}
                            />
                            <hr />
                            <ControlledRowTextField
                                label="Max Days to Schedule Orders Ahead"
                                inputName="maxDaysToScheduleAhead"
                                inputType="number"
                                control={control}
                                rules={{ required: "Max Days to Schedule Orders Ahead" }}
                                isDisabled={!isEditMode}
                            />
                            {errors.maxDaysToScheduleAhead && (
                                <p className="text-danger">
                                    {errors.maxDaysToScheduleAhead.message as string}
                                </p>
                            )}
                            <hr />
                            <Row className="align-items-center">
                                <Col lg="4">
                                    <Label className="pt-1 form-label">Dine Type</Label>
                                </Col>
                                <Col lg="8">
                                    <Select
                                        options={dineTypeForMenu}
                                        value={selectedDineType}
                                        onChange={handleDineTypeChange}
                                        placeholder="Select Dine Type"
                                        isSearchable
                                        isDisabled={!isEditMode}
                                    />
                                </Col>
                            </Row>
                            {dineTypeError && (
                                <Row className="mt-2">
                                    <Col>
                                        <div className="text-danger">{dineTypeError}</div>
                                    </Col>
                                </Row>
                            )}
                            <hr />
                            <Row className="align-items-center">
                                <Col lg="4">
                                    <Label className="pt-1 form-label">Supported Apps</Label>
                                </Col>
                                <Col lg="8">
                                    <Select
                                        options={MENU_SUPPORTED_APPS}
                                        value={selectedSupportedApps}
                                        onChange={handleSupportedAppsChange}
                                        placeholder="Select Supported Apps"
                                        isSearchable
                                        isDisabled={!isEditMode}
                                        isMulti={true}
                                    />
                                </Col>
                            </Row>
                            {supportedAppsError && (
                                <Row className="mt-2">
                                    <Col>
                                        <div className="text-danger">{supportedAppsError}</div>
                                    </Col>
                                </Row>
                            )}
                            <hr />
                        </Form>
                    </CardBody>
                </Card>
                <Card className="mt-3">
                    <CardBody className="mt-3">
                        <div className="selected-items-container">
                            <Row className="align-items-center">
                                <Col sm={3}>
                                    <Label className="pt-1 form-label">Menu Items</Label>
                                </Col>
                                <Col sm={2}>
                                    <Select
                                        value={selectedMenuTypeOption}
                                        onChange={handleMenuTypeChange}
                                        options={MenuTypeForMenu}
                                        placeholder="Menu Type"
                                        isSearchable
                                        isDisabled={!isEditMode}
                                    />
                                </Col>
                                <Col sm={3}>
                                    <Select
                                        value={selectedOption}
                                        onChange={handleItemChange}
                                        options={availableMenuItemsOptions}
                                        placeholder={isLoading ? "Loading..." : "Search menu item"}
                                        isSearchable
                                        isDisabled={!selectedMenuTypeOption || !isEditMode}
                                        loadingMessage={() => "Loading menu items..."}
                                        noOptionsMessage={() =>
                                            isLoading ? "Loading..." : "No items found"
                                        }
                                        filterOption={customFilterOption}
                                    />
                                </Col>
                                <Col sm={2}>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Override Price"
                                        value={overridePrice}
                                        onChange={(e) => setOverridePrice(e.target.value)}
                                        disabled={!selectedOption || !isEditMode}
                                        step="0.01"
                                        min="0"
                                    />
                                </Col>
                                <Col sm={2} className="text-right">
                                    <ButtonComponent
                                        titel="+Add Item"
                                        type="button"
                                        onClick={handleAdd}
                                        isDisabled={
                                            !selectedMenuTypeOption || !selectedOption || !isEditMode
                                        }
                                    />
                                </Col>
                            </Row>
                            {itemError && (
                                <Row className="mt-2">
                                    <Col>
                                        <div className="text-danger">{itemError}</div>
                                    </Col>
                                </Row>
                            )}
                            <hr />
                            <h5>Selected Items</h5>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table table-bordered mt-3">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '50px' }}>Order</th>
                                            <th>Name</th>
                                            <th>Tiny Id</th>
                                            <th>Price (Rs.)</th>
                                            <th>Override Price (Rs.)</th>
                                            <th>Food Attributes</th>
                                            <th>Allergen Information</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedItems.map((item: any, index) => (
                                            <tr
                                                key={index}
                                                draggable={isEditMode}
                                                onDragStart={() => handleDragStart(index)}
                                                onDragOver={(e) => handleDragOver(e, index)}
                                                onDrop={(e) => handleDrop(e, index)}
                                                onDragEnd={handleDragEnd}
                                                style={{
                                                    backgroundColor: draggedIndex === index ? '#e3f2fd' : 'white',
                                                    opacity: draggedIndex === index ? 0.5 : 1,
                                                    cursor: isEditMode ? 'move' : 'default'
                                                }}
                                            >
                                                <td
                                                    style={{
                                                        textAlign: 'center',
                                                        verticalAlign: 'middle',
                                                        cursor: isEditMode ? 'move' : 'default'
                                                    }}
                                                >
                                                    {isEditMode ? (
                                                        <FontAwesomeIcon
                                                            icon={faGripVertical}
                                                            color="#666"
                                                            size="lg"
                                                        />
                                                    ) : (
                                                        index + 1
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-3">{item.displayName}</div>
                                                    <span className='text-warning' style={{ fontSize: '0.875rem' }}>
                                                        {item.itemType === 'SetMenu' ? 'Combo' : item.itemType === 'MenuItem' ? 'Menu Item' : 'Customize'}
                                                    </span>
                                                </td>
                                                <td>{item?.tinyId}</td>
                                                <td>{item.price.toFixed(2)}</td>
                                                <td>
                                                    {isEditMode ? (
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="-"
                                                            value={item.overridePrice || ""}
                                                            onChange={(e) => handleOverridePriceChange(index, e.target.value)}
                                                            step="0.01"
                                                            min="0"
                                                            style={{ width: '120px' }}
                                                        />
                                                    ) : (
                                                        item.overridePrice ? item.overridePrice.toFixed(2) : "-"
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        {item.foodAttributes?.length ? (
                                                            item.foodAttributes.map(
                                                                (attribute: any, i: any) => (
                                                                    <img
                                                                        key={i}
                                                                        src={getImageUrl(attribute)}
                                                                        alt={attribute}
                                                                        width="24"
                                                                        height="24"
                                                                        title={attribute}
                                                                    />
                                                                )
                                                            )
                                                        ) : (
                                                            <span className='text-secondary sm'>No</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        {item.allergenInformation?.length ? (
                                                            item.allergenInformation.map(
                                                                (attribute: any, i: any) => (
                                                                    <img
                                                                        key={i}
                                                                        src={getImageUrl(attribute)}
                                                                        alt={attribute}
                                                                        width="24"
                                                                        height="24"
                                                                        title={attribute}
                                                                    />
                                                                )
                                                            )
                                                        ) : (
                                                            <span className='text-secondary sm'>No</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="light"
                                                        type="button"
                                                        onClick={() => handleDelete(index)}
                                                        disabled={!isEditMode}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} color="#F24E1E" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </CardBody>
                </Card>
                <div className="d-flex justify-content-end mt-4">
                    {isSaving ? (
                        <ButtonComponent
                            icon={<FontAwesomeIcon icon={faSpinner} spin />}
                            titel="Saving..."
                            isDisabled={true}
                        />
                    ) : (
                        <ButtonComponent
                            titel="Update Menu"
                            type="submit"
                            onClick={handleSubmit(onSubmit)}
                            isDisabled={!isEditMode}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default UpdateMenus;
